import { ApolloError, useApolloClient } from '@apollo/client';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { isNotFoundError } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import {
  LiabilitiesIssuersAndRecipientsDocument,
  LiabilitiesIssuersAndRecipientsQuery,
  LiabilitiesIssuersAndRecipientsQueryVariables,
} from '@/modules/liabilities/LiabilityModal/graphql/LiabilityModal.generated';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { getNodes } from '@/utils/graphqlUtils';

import { LiabilitiesStepFormShape } from '../LiabilitiesStep.types';

/** Refreshes the dropdowns and clears missing selections without losing the import form. */
export function useLiabilityImportRecovery(
  householdId: string,
  formMethods: UseFormReturn<LiabilitiesStepFormShape>
) {
  const client = useApolloClient();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  return async (error: unknown): Promise<boolean> => {
    if (
      !(error instanceof ApolloError) ||
      error.networkError ||
      !isNotFoundError(error)
    ) {
      return false;
    }

    try {
      // The options belong to Household.entities, not ROOT_QUERY.entities.
      const { data } = await client.query<
        LiabilitiesIssuersAndRecipientsQuery,
        LiabilitiesIssuersAndRecipientsQueryVariables
      >({
        query: LiabilitiesIssuersAndRecipientsDocument,
        variables: { where: { id: householdId } },
        fetchPolicy: 'network-only',
      });
      const household = getNodes(data.households)[0];
      const availableIds = new Set(
        getNodes(household?.entities).map((entity) => entity.id)
      );
      availableIds.add(NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL);

      for (const [id, row] of Object.entries(
        formMethods.getValues('liabilities')
      )) {
        for (const field of ['issuerId', 'recipientId'] as const) {
          if (row[field] && !availableIds.has(row[field])) {
            const path = `liabilities.${id}.${field}` as const;
            formMethods.setValue(path, '', {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }
      }
      showFeedback(
        'A selected entity is no longer available. The options have been refreshed. Select it again before importing.',
        { variant: 'warning' }
      );
    } catch (refreshError) {
      reportError(
        'error refreshing liability import options',
        refreshError instanceof Error
          ? refreshError
          : new Error(String(refreshError))
      );
      showFeedback(
        'A selected entity is no longer available. We could not refresh the options. Refresh the page before importing.',
        { variant: 'error' }
      );
    }
    return true;
  };
}
